import React, { useRef } from 'react';
import type { VoiceResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import { VoiceIconBox, ProviderBadge, VoiceStatusPill, getProviderStyle } from './VoiceBadges';

export type VoiceCardAction = 'edit' | 'delete' | 'toggleStatus';

/**
 * Card de voz (view "Cards" + fallback mobile da view "Lista"). Padrão-lista
 * (F34): SEM checkbox — seleção em massa vive só na view de tabela. Menu •••
 * de ações. Identidade visual = caixa de ícone (mic) colorida pelo provider.
 */
export interface VoiceCardProps {
  voice: VoiceResponse;
  onAction?: (action: VoiceCardAction, voice: VoiceResponse) => void;
}

export const VoiceCard: React.FC<VoiceCardProps> = ({ voice, onAction }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const playSample = () => { void audioRef.current?.play(); };

  const actions: CardAction[] = [];
  if (voice.audioSampleUrl) {
    actions.push({ label: 'Ouvir amostra', icon: 'play', onClick: playSample });
  }
  if (onAction) {
    actions.push({ label: 'Editar', icon: 'edit', onClick: () => onAction('edit', voice) });
    actions.push({
      label: voice.status === 'active' ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onAction('toggleStatus', voice),
    });
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onAction('delete', voice), variant: 'danger' });
  }

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`voice-card-${voice.id}`}
    >
      {/* Cabeçalho: ícone + nome + ações */}
      <div className="flex items-start gap-3">
        <VoiceIconBox providerId={voice.providerId} />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {voice.name}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {getProviderStyle(voice.providerId).label}
          </p>
        </div>
        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`voice-actions-${voice.id}`} />
          </div>
        )}
      </div>

      {/* Descrição */}
      <p className="mt-3 text-sm line-clamp-2" style={{ color: 'var(--theme-muted-text)' }}>
        {voice.description || 'Sem descrição'}
      </p>

      {/* Rodapé: ouvir amostra + provider + status */}
      <div
        className="mt-3 pt-3 flex items-center gap-2"
        style={{ borderTop: '1px solid var(--theme-card-border)' }}
      >
        {voice.audioSampleUrl && (
          <>
            <button
              type="button"
              onClick={playSample}
              className="inline-flex items-center gap-1.5 h-[30px] px-3 rounded-lg text-xs font-semibold transition-colors flex-shrink-0"
              style={{ border: '1px solid var(--theme-input-border)', color: 'var(--theme-card-text)' }}
              data-testid={`voice-play-${voice.id}`}
            >
              <Icon name="play" className="w-[13px] h-[13px]" />
              Ouvir amostra
            </button>
            <audio ref={audioRef} src={voice.audioSampleUrl} className="hidden" data-testid={`voice-audio-${voice.id}`} />
          </>
        )}
        <ProviderBadge providerId={voice.providerId} />
        <span className="ml-auto">
          <VoiceStatusPill status={voice.status} />
        </span>
      </div>
    </div>
  );
};

VoiceCard.displayName = 'VoiceCard';
